import * as THREE from "three";
import { DIST_MAX, DIST_MIN, type CameraRig } from "../render/camera";
import type { Hit } from "../tools/act";
import type { GrabTool } from "../tools/grab";
import type { PlaceTool } from "../tools/place";
import type { Mode } from "./keys";

interface PinchInfo {
  span: number;
  angle: number;
  mx: number;
  my: number;
}
interface PointerStart {
  sx: number;
  sy: number;
  t: number;
  touch: boolean;
}
type Gesture =
  | { type: "pinch"; start: PinchInfo; prev: PinchInfo; dist: number; theta: number; t: number }
  | ({ type: "pending" | "orbit" | "panPending" | "pan" | "grab" } & PointerStart)
  | ({ type: "ghost"; ox: number; oz: number } & PointerStart)
  | { type: "done" };

interface PointerDeps {
  rig: CameraRig;
  place: PlaceTool;
  grab: GrabTool;
  pick: (x: number, y: number) => Hit | null;
  mode: () => Mode;
  /** A short press without a drag: what it does depends on the tool. */
  onTap: (e: PointerEvent) => void;
  /** The last input switched between mouse and touch. */
  onInputChange: () => void;
  onLeave: () => void;
}

/* =====================================================================
   POINTER INPUT
   Mouse: left-click acts, left-drag orbits, right/middle/Shift-drag pans,
          right-click centres, scroll zooms toward the cursor.
   Touch: tap aims or selects and a button confirms, one finger orbits,
          two fingers pinch/pan/twist, a two-finger tap centres.
   ===================================================================== */
export function initPointer(canvas: HTMLCanvasElement, deps: PointerDeps) {
  const { rig, place, grab, pick, mode } = deps;
  const { camT, panBy, zoomAt } = rig;
  const pointers = new Map<number, { x: number; y: number }>();
  const input = {
    touch: matchMedia("(pointer: coarse)").matches, // was the last input a touch?
    mouse: null as { x: number; y: number } | null, // where the mouse rests, if over the canvas
    gesture: null as Gesture | null,
  };

  function pinchInfo(): PinchInfo {
    const [a, b] = [...pointers.values()];
    return {
      span: Math.hypot(b.x - a.x, b.y - a.y),
      angle: Math.atan2(b.y - a.y, b.x - a.x),
      mx: (a.x + b.x) / 2,
      my: (a.y + b.y) / 2,
    };
  }
  function focusAt(x: number, y: number) {
    const hit = pick(x, y);
    const p = hit ? hit.point : rig.screenToPlane(x, y, 0);
    if (p) rig.focusOn(p);
  }

  canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  canvas.addEventListener("pointerdown", (e) => {
    const touch = e.pointerType !== "mouse";
    if (touch !== input.touch) {
      input.touch = touch;
      deps.onInputChange();
    }
    canvas.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) {
      grab.end(); // a second finger lets go of a held block
      const p = pinchInfo();
      input.gesture = {
        type: "pinch",
        start: p,
        prev: p,
        dist: camT.dist,
        theta: camT.theta,
        t: performance.now(),
      };
      return;
    }
    if (pointers.size > 2) return;
    const base: PointerStart = { sx: e.clientX, sy: e.clientY, t: performance.now(), touch };
    const hit = mode() === "grab" ? pick(e.clientX, e.clientY) : null;
    if (!touch && (e.button === 1 || e.button === 2 || e.shiftKey))
      input.gesture = { type: "panPending", ...base };
    else if (!touch && e.button !== 0) input.gesture = null;
    else if (hit) {
      grab.start(hit); // pressing on a block picks it up
      input.gesture = { type: "grab", ...base };
    } else if (touch && mode() === "place" && place.nearGhost(e.clientX, e.clientY)) {
      // Dragging the preview: remember the offset so it doesn't jump to the finger
      const h0 = place.planePoint(e.clientX, e.clientY);
      const aim = place.aim;
      input.gesture = {
        type: "ghost",
        ...base,
        ox: h0 ? aim.x - h0.x : 0,
        oz: h0 ? aim.z - h0.z : 0,
      };
    } else input.gesture = { type: "pending", ...base };
  });

  canvas.addEventListener("pointermove", (e) => {
    if (e.pointerType === "mouse") input.mouse = { x: e.clientX, y: e.clientY };
    const p = pointers.get(e.pointerId);
    if (!p) {
      // plain mouse hover
      if (e.pointerType === "mouse" && mode() === "place") place.aimAt(e.clientX, e.clientY);
      return;
    }
    const dx = e.clientX - p.x,
      dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;
    const g = input.gesture;
    if (!g || g.type === "done") return;
    if (g.type === "pinch") {
      if (pointers.size < 2) return;
      const now = pinchInfo();
      camT.dist = THREE.MathUtils.clamp(
        (g.dist * g.start.span) / Math.max(now.span, 1),
        DIST_MIN,
        DIST_MAX,
      );
      let da = now.angle - g.start.angle;
      da = Math.atan2(Math.sin(da), Math.cos(da));
      camT.theta = g.theta - da; // twist rotates the view
      panBy(now.mx - g.prev.mx, now.my - g.prev.my);
      g.prev = now;
      return;
    }
    const moved = Math.hypot(e.clientX - g.sx, e.clientY - g.sy);
    // Fingers wobble more than mice, so touch needs a bigger move to count as a drag
    if (g.type === "pending" && moved > (g.touch ? 12 : 6)) g.type = "orbit";
    else if (g.type === "panPending" && moved > 4) g.type = "pan";
    if (g.type === "orbit") rig.orbitBy(dx, dy);
    else if (g.type === "pan") panBy(dx, dy);
    else if (g.type === "grab") grab.move(e.clientX, e.clientY);
    else if (g.type === "ghost") {
      const h = place.planePoint(e.clientX, e.clientY);
      if (h) place.setAim(h.x + g.ox, h.z + g.oz);
    }
  });

  function endPointer(e: PointerEvent, cancelled: boolean) {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);
    const g = input.gesture;
    if (g && g.type === "grab") grab.end();
    if (g && g.type === "pinch") {
      const moved =
        Math.hypot(g.prev.mx - g.start.mx, g.prev.my - g.start.my) +
        Math.abs(g.prev.span - g.start.span);
      if (!cancelled && performance.now() - g.t < 300 && moved < 20)
        focusAt(g.start.mx, g.start.my); // two-finger tap
      input.gesture = { type: "done" }; // ignore the remaining finger until it lifts
    } else if (g && !cancelled) {
      // A slow press on touch is treated as a hold, not a tap
      if (g.type === "pending" && !(g.touch && performance.now() - g.t > 450)) deps.onTap(e);
      else if (g.type === "panPending") focusAt(e.clientX, e.clientY); // right-click centres the view
    }
    if (pointers.size === 0) input.gesture = null;
    else if (input.gesture && input.gesture.type !== "done") input.gesture = { type: "done" };
  }
  canvas.addEventListener("pointerup", (e) => endPointer(e, false));
  canvas.addEventListener("pointercancel", (e) => endPointer(e, true));
  canvas.addEventListener("pointerleave", () => {
    input.mouse = null;
    deps.onLeave();
  });

  canvas.addEventListener(
    "wheel",
    (e) => {
      e.preventDefault();
      let d = e.deltaY || 0;
      if (e.deltaMode === 1) d *= 33; // line-based scrolling (Firefox)
      if (e.shiftKey) {
        // Shift + scroll adjusts drop height
        place.nudgeClearance(-Math.sign(d || e.deltaX));
        return;
      }
      if (grab.held) {
        grab.lift(-Math.sign(d) * 0.15);
        return;
      } // while holding, scroll lifts and lowers
      zoomAt(Math.exp(THREE.MathUtils.clamp(d, -120, 120) * 0.0015), e.clientX, e.clientY);
    },
    { passive: false },
  );

  return input;
}
